import { CitationMarker } from "@design";
import { type ComponentProps, createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Markdown, type MarkdownComponents, type MarkdownRemarkPlugins } from "@/components/chat/Markdown";
import { MarkdownLink } from "@/components/chat/MarkdownMedia";
import { wikiPagePath } from "@/features/knowledge/KnowledgeTabs";
import { citationNumber, remarkCitations } from "./citations";
import type { ChatSource, NumberedSources } from "./messageSources";
import { formatPublishedDate, sourceSection } from "./Sources";

const SourcesContext = createContext<NumberedSources>([]);

function openLabelKey(source: ChatSource) {
  if (source.wiki) return "sources.openPage" as const;
  return source.type === "rag" ? ("message.openInKnowledge" as const) : ("sources.open" as const);
}

/** A `#cite-<n>` link (see citations.ts) is a source popover; any other link stays a normal chat link. */
function CitationLink(props: Readonly<ComponentProps<"a"> & { node?: unknown }>) {
  const sources = useContext(SourcesContext);
  const { t } = useTranslation("chat");
  const navigate = useNavigate();
  const number = citationNumber(props.href);
  const source = number ? sources[number - 1] : null;
  if (!number || !source) return <MarkdownLink {...props} />;
  const wiki = source.wiki;
  const site = sourceSection(source);
  const published = formatPublishedDate(source.publishedDate);
  return (
    <CitationMarker
      number={number}
      title={source.name}
      site={site && published ? t("sources.siteWithDate", { site, date: published }) : (site ?? published)}
      snippet={source.snippet}
      href={wiki ? undefined : source.url}
      onOpen={wiki ? () => navigate(wikiPagePath(wiki.spaceUuid, wiki.path)) : undefined}
      labels={{ marker: (n) => t("sources.marker", { number: n }), open: t(openLabelKey(source)) }}
    />
  );
}

const COMPONENTS: MarkdownComponents = { a: CitationLink };

/**
 * An answer's markdown with its `[n]` citations shown as superscript markers that preview source n (hover, focus
 * or tap). Without sources it's plain `Markdown`, rendered exactly as before.
 */
export function CitedMarkdown({
  text,
  sources,
  streaming = false,
}: Readonly<{ text: string; sources: NumberedSources; streaming?: boolean }>) {
  // Which numbers resolve ("1101"): the plugin, and so every rendered block, only changes when that does.
  const resolvable = sources.map((source) => (source ? "1" : "0")).join("");
  const plugins = useMemo<MarkdownRemarkPlugins>(() => [[remarkCitations, { resolvable }]], [resolvable]);
  if (!resolvable.includes("1")) return <Markdown streaming={streaming}>{text}</Markdown>;
  return (
    <SourcesContext.Provider value={sources}>
      <Markdown streaming={streaming} remarkPlugins={plugins} components={COMPONENTS}>
        {text}
      </Markdown>
    </SourcesContext.Provider>
  );
}
